import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUser } from "@/features/auth/hooks/use-user";
import {
  useDeleteDocument,
  useReEmbedDocument,
} from "@/features/documents/hooks/use-documents";
import type { Document } from "@/features/documents/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  ArrowsClockwiseIcon,
  DotsThreeOutlineIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

export const Action = ({ document }: { document: Document }) => {
  const { t } = useTranslation("documents");
  const { company: clientId } = useParams<{ company: string }>();
  const deleteDocument = useDeleteDocument();
  const reEmbedDocument = useReEmbedDocument();
  const { data: user } = useUser();

  const handleDelete = async () => {
    if (!clientId) return;

    try {
      await deleteDocument.mutateAsync(document.id);
      toast.success(t("messages.deleteSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.deleteError"),
      );
    }
  };

  const handleReEmbed = async () => {
    if (!clientId) return;

    try {
      await reEmbedDocument.mutateAsync(document.id);
      toast.success(t("messages.reEmbedSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.reEmbedError"),
      );
    }
  };

  const canReEmbed = user?.role === "SYSADMIN";

  return (
    <div className="text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <DotsThreeOutlineIcon weight="fill" className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => navigator.clipboard.writeText(document.id)}
          >
            {t("actions.copyId")}
          </DropdownMenuItem>
          {canReEmbed && (
            <DropdownMenuItem onClick={handleReEmbed}>
              <ArrowsClockwiseIcon className="h-4 w-4 mr-2" />
              {t("actions.reEmbed")}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="p-0">
            <Button
              variant="ghost"
              onClick={handleDelete}
              className="w-full justify-start text-destructive hover:text-destructive"
              confirm={t("messages.deleteConfirm", {
                filename: document.filename,
              })}
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              {t("actions.delete")}
            </Button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
